import { ReactNode, ComponentType, useEffect } from 'react'
import {
  faArrowsRotate,
  faFileArrowUp,
  faArrowUpArrowDown,
  faUpRightFromSquare,
  faBolt,
  faChartBar,
} from '@fortawesome/pro-solid-svg-icons'
import {
  Banner,
  BannerVariants,
  Button,
  Card,
  CardColors,
  Dialog,
  Image,
  ListCustomBullets,
  Text,
  Link,
} from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { HEARD_BANKING_LEARN_MORE_URL } from '@/features/banking/banking.constants'
import {
  OFFER_SOURCES,
  useBankingAnalytics,
} from '@/features/banking/analytics'
import { FiTier, type SelectedInstitution } from './bank-compatibility.types'

interface BankCompatibilityDialogProps {
  institution: SelectedInstitution | null
  onClose: () => void
}

const HEARD_BANKING_ILLUSTRATION =
  'https://heard-images.s3.amazonaws.com/assets/piggy-bank-card.svg'

const PERSONAL_PROMO_BODY =
  "Apply in minutes once you're a member, and see how it feels to have banking and bookkeeping in one place."
const UNSUPPORTED_PROMO_BODY =
  'After you signup, apply in minutes and skip manual uploads for good.'

const HeardBankingPromoCard = ({
  body,
  onLearnMore,
}: {
  body: string
  onLearnMore: () => void
}) => (
  <Card color={CardColors.EMPHASIS} className="mb-4">
    <div className="flex items-center gap-4">
      <div className="flex-1 flex flex-col items-start">
        <Text
          variant={TypographyStyles.EYEBROW}
          color={TextColors.MUTED}
          className="mb-1"
        >
          Free member benefit
        </Text>
        <Text variant={TypographyStyles.DISPLAY_SM}>
          Try Heard Business Banking
        </Text>
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.MUTED}
          className="mb-4"
        >
          FDIC Insured
        </Text>
        <Text variant={TypographyStyles.BODY_SM} className="mb-4">
          {body}
        </Text>
        <Button iconRight={faUpRightFromSquare} onClick={onLearnMore}>
          Learn More
        </Button>
      </div>
      <Image
        src={HEARD_BANKING_ILLUSTRATION}
        alt="Heard Banking"
        className="w-45 h-45 shrink-0 self-start"
      />
    </div>
  </Card>
)

const BusinessAccountCallout = ({
  onLearnMore,
}: {
  onLearnMore: () => void
}) => (
  <Banner variant={BannerVariants.DEFAULT} wrapperClassName="w-full">
    <span className="font-bold">Want to try Heard Banking?</span> It&apos;s
    included in your plan. Apply in minutes and see how it feels to have banking
    and bookkeeping in one place.{' '}
    <Link href={HEARD_BANKING_LEARN_MORE_URL} newPage onClick={onLearnMore}>
      Learn more &rarr;
    </Link>
  </Banner>
)

const PersonalAccountBullet = () => (
  <ListCustomBullets.Item
    icon={faArrowUpArrowDown}
    iconBackgroundColor="priority-bold"
    iconSize="sm"
    className="items-center"
  >
    <Text variant={TypographyStyles.BODY_SM} className="text-neutral-600">
      Without a dedicated business banking account, you&apos;ll need to separate
      your personal transactions from your business expenses <b>each month</b>.
    </Text>
  </ListCustomBullets.Item>
)

const GreenDialogContent = ({
  prependBullet,
}: {
  prependBullet?: ReactNode
}) => (
  <>
    <Text variant={TypographyStyles.BODY_SM}>
      Great! We&apos;ll be able to fully support you. What this means:
    </Text>
    <ListCustomBullets variant="icon" className="pl-14">
      {prependBullet}
      <ListCustomBullets.Item
        icon={faBolt}
        iconBackgroundColor="success-bold"
        iconSize="sm"
        className="items-center"
        description="Instant access to past transactions"
      />
      <ListCustomBullets.Item
        icon={faArrowsRotate}
        iconBackgroundColor="success-bold"
        iconSize="sm"
        className="items-center"
        description="Automatic transaction syncing"
      />
      <ListCustomBullets.Item
        icon={faChartBar}
        iconBackgroundColor="success-bold"
        iconSize="sm"
        className="items-center"
        description="Income and expenses categorized"
      />
    </ListCustomBullets>
  </>
)

const YellowDialogContent = ({
  name,
  prependBullet,
}: {
  name: string
  prependBullet?: ReactNode
}) => (
  <>
    <Text variant={TypographyStyles.BODY_SM}>
      Great! We can support you. {name} syncs your transactions automatically,
      but not your balance.
    </Text>
    <ListCustomBullets variant="icon" className="pl-14">
      {prependBullet}
      <ListCustomBullets.Item
        icon={faFileArrowUp}
        iconBackgroundColor="warning-yellow"
        iconSize="sm"
        className="items-center"
      >
        <Text variant={TypographyStyles.BODY_SM} className="text-neutral-600">
          A <b>one-time statement upload</b> is all we need to establish your
          opening balance.
        </Text>
      </ListCustomBullets.Item>
    </ListCustomBullets>
    <Text variant={TypographyStyles.BODY_SM} className="mt-2">
      After that, you&apos;ll get:
    </Text>
    <ListCustomBullets variant="icon" className="pl-14">
      <ListCustomBullets.Item
        icon={faArrowsRotate}
        iconBackgroundColor="emphasis-bold"
        iconSize="sm"
        className="items-center"
        description="Automatic transaction syncing going forward"
      />
      <ListCustomBullets.Item
        icon={faChartBar}
        iconBackgroundColor="emphasis-bold"
        iconSize="sm"
        className="items-center"
        description="Income and expenses categorized to catch tax deductions"
      />
    </ListCustomBullets>
  </>
)

const RedDialogContent = ({
  name,
  prependBullet,
  hideRecommendedBanksFooter,
}: {
  name: string
  prependBullet?: ReactNode
  hideRecommendedBanksFooter?: boolean
}) => (
  <>
    <Text variant={TypographyStyles.BODY_SM}>
      {name} is not supported by Plaid, which is how we automate your
      experience. What this means:
    </Text>
    <ListCustomBullets variant="icon" className="pl-14">
      {prependBullet}
      <ListCustomBullets.Item
        icon={faFileArrowUp}
        iconBackgroundColor="priority-bold"
        iconSize="sm"
        className="items-center"
      >
        <Text variant={TypographyStyles.BODY_SM} className="text-neutral-600">
          You&apos;ll need to <b>upload past statements</b> for us to accurately
          reconstruct and reconcile your books.
        </Text>
      </ListCustomBullets.Item>
      <ListCustomBullets.Item
        icon={faFileArrowUp}
        iconBackgroundColor="priority-bold"
        iconSize="sm"
        className="items-center"
      >
        <Text variant={TypographyStyles.BODY_SM} className="text-neutral-600">
          You&apos;ll also need to upload statements manually <b>each month</b>{' '}
          for us to keep your books accurate.
        </Text>
      </ListCustomBullets.Item>
    </ListCustomBullets>
    {!hideRecommendedBanksFooter && (
      <Text variant={TypographyStyles.BODY_SM} className="text-neutral-400">
        Switching to a supported bank removes the manual work and unlocks the
        full Heard experience.{' '}
        <Link
          href="https://support.joinheard.com/hc/en-us/articles/23407015266967-Using-a-dedicated-business-bank-account-for-your-practice#open_business_account"
          newPage
        >
          See recommended banks &rarr;
        </Link>
      </Text>
    )}
  </>
)

const DIALOG_TITLES: Record<FiTier, (name: string) => string> = {
  [FiTier.WELL_SUPPORTED]: (name) => `${name} is fully compatible`,
  [FiTier.LIMITED_SUPPORT]: () => 'Your bank is compatible, with data limits',
  [FiTier.NOT_SUPPORTED]: () => 'Your bank is not compatible',
}

const DIALOG_CONTENT: Record<
  FiTier,
  ComponentType<{
    name: string
    prependBullet?: ReactNode
    hideRecommendedBanksFooter?: boolean
  }>
> = {
  [FiTier.WELL_SUPPORTED]: GreenDialogContent,
  [FiTier.LIMITED_SUPPORT]: YellowDialogContent,
  [FiTier.NOT_SUPPORTED]: RedDialogContent,
}

export const BankCompatibilityDialog = ({
  institution,
  onClose,
}: BankCompatibilityDialogProps) => {
  const { trackOfferViewed, trackOfferClicked } = useBankingAnalytics()

  const isPersonalAccount = institution?.accountSubtype === 'personal'
  const isUnsupportedBusinessAccount =
    institution?.accountSubtype === 'business' &&
    institution?.tier === FiTier.NOT_SUPPORTED

  const showHeardBankingPromo =
    isPersonalAccount || isUnsupportedBusinessAccount
  const showBusinessAccountCallout =
    institution?.accountSubtype === 'business' &&
    institution?.tier !== undefined &&
    institution.tier !== FiTier.NOT_SUPPORTED
  const bankingOfferRendered =
    showHeardBankingPromo || showBusinessAccountCallout

  // Fire the offer-view event only when the conditional banking section
  // actually renders — not on every dialog open.
  useEffect(() => {
    if (bankingOfferRendered) {
      trackOfferViewed(OFFER_SOURCES.SignupBankCompatibilityModal)
    }
  }, [bankingOfferRendered, trackOfferViewed])

  if (!institution) return null

  const Content = DIALOG_CONTENT[institution.tier]
  const title = DIALOG_TITLES[institution.tier](institution.name)
  const promoBody = isUnsupportedBusinessAccount
    ? UNSUPPORTED_PROMO_BODY
    : PERSONAL_PROMO_BODY

  const handleLearnMore = () => {
    trackOfferClicked(OFFER_SOURCES.SignupBankCompatibilityModal)
    window.open(HEARD_BANKING_LEARN_MORE_URL, '_blank', 'noopener')
  }

  const handleCalloutClick = () => {
    trackOfferClicked(OFFER_SOURCES.SignupBankCompatibilityModal)
  }

  return (
    <Dialog
      open={Boolean(institution)}
      onOpenChange={(open) => !open && onClose()}
    >
      <Dialog.Content size="lg" heading={title} onDismiss={onClose}>
        {showHeardBankingPromo && (
          <HeardBankingPromoCard
            body={promoBody}
            onLearnMore={handleLearnMore}
          />
        )}
        <Content
          name={institution.name}
          prependBullet={
            isPersonalAccount ? <PersonalAccountBullet /> : undefined
          }
          hideRecommendedBanksFooter={showHeardBankingPromo}
        />
        {showBusinessAccountCallout && (
          <BusinessAccountCallout onLearnMore={handleCalloutClick} />
        )}
        <Dialog.Footer>
          <Button onClick={onClose}>Got it</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
